import { useEffect } from "react"
import {
  GroupBase,
  MenuProps,
  OptionProps,
} from "react-select"
import { clx, CheckMiniIcon } from "~/design-system"

export const Menu = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>({
  className,
  cx,
  children,
  innerProps,
  innerRef,
  placement,
  selectProps: { onMenuClose, menuIsOpen },
}: MenuProps<Option, IsMulti, Group>) => {
  useEffect(() => {
    const closeOnResize = () => {
      if (menuIsOpen) {
        onMenuClose()
      }
    }

    window.addEventListener("resize", closeOnResize)

    return () => {
      window.removeEventListener("resize", closeOnResize)
    }
  }, [menuIsOpen, onMenuClose])

  return (
    <div
      {...innerProps}
      // A global fix to fix scrolling menu options on a reactflow canvas.
      onWheelCapture={(event) => {
        event.stopPropagation()
        innerProps?.onWheel?.(event)
      }}
      ref={innerRef}
      className={clx(
        "bg-grey-0 rounded-md shadow-elevation-flyout mb-8 absolute z-[73] w-full overflow-hidden",
        {
          "top-[calc(100%+8px)]": placement === "bottom",
          "bottom-full": placement === "top",
        },
        className,
      )}
    >
      {children}
    </div>
  )
}

export const Option = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>(
  props: OptionProps<Option, IsMulti, Group>
) => {
  const {
    isSelected,
    isDisabled,
    isFocused,
    children,
    cx,
    className,
    innerProps,
    innerRef,
    selectProps: { hideSelectedOptions, isMulti },
  } = props
  return (
    <div
      role="button"
      className={clx(
        "py-2 px-base flex items-center justify-between transition-colors overflow-hidden whitespace-nowrap",
        {
          "hover:bg-grey-10": !isDisabled,
          "text-ui-fg-muted cursor-not-allowed select-none pointer-events-none": isDisabled,
          "bg-grey-10": isFocused && !isDisabled,
          hidden: hideSelectedOptions && isSelected,
        },
        className
      )}
      ref={innerRef}
      data-diabled={isDisabled ? true : undefined}
      aria-disabled={isDisabled ? true : undefined}
      tabIndex={isDisabled ? -1 : 0}
      {...innerProps}
    >
      <div className="gap-x-small flex flex-1 items-center">
        {isMulti && (
          <CheckboxAdornment isSelected={isSelected} isDisabled={isDisabled} />
        )}
        <div
          className={clx(
            "gap-x-2 txt-base-regular flex flex-1 items-center justify-between"
          )}
        >
          <span className="w-full">{children}</span>
        </div>
      </div>
      {!isMulti && (
        <div className="ml-2 w-5">
          {isSelected && <CheckMiniIcon />}
        </div>
      )}
    </div>
  )
}

const CheckboxAdornment = ({
  isSelected,
  isDisabled,
}: Pick<OptionProps, "isSelected" | "isDisabled">) => {
  return (
    <div
      className={clx(
        `w-4 h-4 text-grey-0 border-grey-30 rounded flex justify-center border transition-colors`,
        {
          "bg-blue-base border-blue-base": isSelected,
          "bg-grey-10 border-ui-border-subtle": isDisabled,
        }
      )}
    >
      <span className="self-center">
        {isSelected && <CheckMiniIcon size={16} />}
      </span>
    </div>
  )
}
